/**
 * Canonical PostHog event names.
 * Use these instead of ad-hoc strings for consistency and discoverability.
 */
export const POSTHOG_EVENTS = {
  /** Route or loader error (TanStack Router errorComponent) */
  ROUTE_ERROR: 'route_error',
  /** React render error (Error Boundary) */
  REACT_ERROR_BOUNDARY: 'react_error_boundary',
  /** E2E smoke test confirmation (CI/deploy) */
  E2E_SMOKE_TEST: 'e2e_smoke_test',
  /** Landing page viewed */
  LANDING_PAGE_VIEWED: 'landing_page_viewed',
  /** Landing page CTA clicked */
  LANDING_CTA_CLICKED: 'landing_cta_clicked',
  /** General feedback survey triggered (persistent button, feedback route) */
  GENERAL_FEEDBACK_TRIGGERED: 'general_feedback_triggered',
  /** Feature feedback survey triggered (feature-specific feedback) */
  FEATURE_FEEDBACK_TRIGGERED: 'feature_feedback_triggered',
  /** First task feedback survey triggered (queue card after first task) */
  FIRST_TASK_FEEDBACK_TRIGGERED: 'first_task_feedback_triggered',
  /** Feedback queue card dismissed without responding */
  FEEDBACK_PROMPT_DISMISSED: 'feedback_prompt_dismissed',
  /** Asset CRUD and search events */
  ASSET_CREATED: 'asset_created',
  ASSET_UPDATED: 'asset_updated',
  ASSET_DELETED: 'asset_deleted',
  ASSET_VIEWED: 'asset_viewed',
  ASSET_SEARCHED: 'asset_searched',
  /** AI analysis events */
  AI_ANALYSIS_TRIGGERED: 'ai_analysis_triggered',
  AI_ANALYSIS_COMPLETED: 'ai_analysis_completed',
  AI_ANALYSIS_FAILED: 'ai_analysis_failed',
  /** Queue item action events */
  QUEUE_ITEM_ACCEPTED: 'queue_item_accepted',
  QUEUE_ITEM_DISMISSED: 'queue_item_dismissed',
  QUEUE_ITEM_DEFERRED: 'queue_item_deferred',
  /** Queue batch events */
  QUEUE_BATCH_COMPLETED: 'queue_batch_completed',
  QUEUE_BATCH_REFILLED: 'queue_batch_refilled',
  /** Queue context flow events */
  QUEUE_CONTEXT_FLOW_STARTED: 'queue_context_flow_started',
  QUEUE_CONTEXT_FLOW_COMPLETED: 'queue_context_flow_completed',
  /** DIY project events (legacy / AI beta — distinct from Living Guide funnel strings below) */
  DIY_PROJECT_CREATED: 'diy_project_created',
  DIY_STEP_COMPLETED: 'diy_step_completed',
  DIY_PROJECT_COMPLETED: 'diy_project_completed',
  DIY_AI_QUESTION_ASKED: 'diy_ai_question_asked',
  DIY_TIP_SUBMITTED: 'diy_tip_submitted',
  DIY_AI_ERROR: 'diy_ai_error',
  /** DIY Living Project Guide — SEO template funnel (canonical names; do not rename strings) */
  DIY_TEMPLATE_PAGE_VIEWED: 'diy_template_page_viewed',
  DIY_PLAN_CUSTOMIZATION_STARTED: 'diy_plan_customization_started',
  DIY_PLAN_CUSTOMIZATION_COMPLETED: 'diy_plan_customization_completed',
  DIY_AI_CHAT_OPENED: 'diy_ai_chat_opened',
  DIY_SAVE_CTA_CLICKED: 'diy_save_cta_clicked',
  DIY_CONVERSION_COMPLETED: 'diy_conversion_completed',
  DIY_TOC_NAVIGATION: 'diy_toc_navigation',
  DIY_RELATED_PROJECT_CLICKED: 'diy_related_project_clicked',
  DIY_FAQ_EXPANDED: 'diy_faq_expanded',
  /** Project guide completion (dashboard / guide — not `diy_project_completed`) */
  PROJECT_COMPLETED: 'project_completed',
  MAINTENANCE_NUDGE_CLICKED: 'maintenance_nudge_clicked',
  DISCOVERY_FEATURE_CLICKED: 'discovery_feature_clicked',
} as const;

/**
 * Property keys for error events.
 * Aligns with PostHog Error Tracking and source map linking.
 */
export const POSTHOG_ERROR_PROPERTIES = {
  ERROR_MESSAGE: 'error_message',
  ERROR_STACK: 'error_stack',
  COMPONENT_STACK: 'component_stack',
  ROUTE_ID: 'route_id',
  FEATURE: 'feature',
  MUTATION: 'mutation',
  ERROR_BOUNDARY: 'error_boundary',
} as const;
